import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { errorToast, warningToast } from "@/ui/toast/Toast";
import { redirect, useNavigate } from "react-router-dom";
import { resolveRequestError } from "@/services/apiErrors";
import i18n from "@/i18n";

// The mapping itself lives in services/apiErrors so it can be tested without a
// router or a toast host. This only carries out what it decided.
function handleRequestError(error, navigate) {
	const { messageKey, message, level, redirectTo } = resolveRequestError(error);
	// Worded now, not when the request started: the language may have changed.
	const text = messageKey ? i18n.t(messageKey) : message;

	if (level === "warning") warningToast(text);
	else if (level === "error") errorToast(text);

	if (redirectTo) navigate(redirectTo, { replace: true });
}

function shouldRetry(failureCount, error, navigate, suppressErrorRedirect) {
	if (error?.response) {
		// Handle response code — unless the caller opted out, because it
		// renders the error itself. Retry behaviour is unchanged either way.
		if (!suppressErrorRedirect) handleRequestError(error, navigate);

		// Don't retry if we reached the server and got a response
		return false;
	}

	// Retry logic for network errors etc.
	// Max 3 requests (2 retries)
	return failureCount < 2;
}

// CAVEAT: `...options` is spread LAST, so a caller passing `options.retry`
// overrides the internal `retry` below. `handleRequestError` — the toast and
// redirect — is invoked from inside that `retry` callback, so such a caller
// bypasses error handling entirely and `suppressErrorRedirect` becomes inert.
// Do not restructure this: the side effect lives in `retry` because
// react-query v5 removed `onError` from `useQuery`, and this hook backs every
// query in the app.
//
// `suppressErrorRedirect` is opt-IN per call site and off everywhere else: the
// toast-and-redirect behaviour is correct for the app's authenticated screens.
// It exists for queries whose failure is an expected state the component
// already renders — see features/courses/hooks/useGetCourse.js, where a 403
// means "this course's first unit isn't a free preview", not "go to /plans".
export const useApiGet = ({
	key,
	fn,
	options,
	suppressErrorRedirect = false,
}) => {
	const navigate = useNavigate();

	return useQuery({
		queryKey: key,
		queryFn: fn,
		retry: (failureCount, error) =>
			shouldRetry(failureCount, error, navigate, suppressErrorRedirect),
		refetchOnMount: false,
		...options,
	});
};

export const useApiSend = ({
	mutationKeys,
	fn,
	onSuccess,
	onError,
	invalidateKeys,
	resetKeys,
	options,
}) => {
	const queryClient = useQueryClient();

	const navigate = useNavigate();

	return useMutation({
		mutationKey: mutationKeys,
		mutationFn: fn,
		onSuccess: (data) => {
			if (onSuccess) onSuccess(data);

			if (invalidateKeys) {
				invalidateKeys.forEach((key) => {
					queryClient.invalidateQueries({ queryKey: key });
				});
			}

			// sometimes it's better to remove them entirely and show the loader
			if (resetKeys) {
				resetKeys.forEach((key) => {
					queryClient.resetQueries({ queryKey: key });
				});
			}
		},
		onError: (error, variables, context) => {
			if (onError) onError(error, variables, context);
		},
		retry: (failureCount, error) =>
			shouldRetry(failureCount, error, navigate),
		...options,
	});
};
